/**
 * Registers v1's built-in intent handlers. Idempotent — safe to call once at
 * app startup. The navigate handler validates its payload against a path
 * allowlist that mirrors the server destination map (defense-in-depth).
 */
import { ATTENTION_ROUTES } from '@shared/constants/routes';

import { registerIntent } from './uiIntentDispatcher';
import { useDocumentViewerStore } from '../chat/stores/useDocumentViewerStore';

// Mirrors the server allowlist in pa.core.ai.ui_intents.destinations. The two
// must move together: the handler fails closed, so a server path missing from
// this set makes the intent silently do nothing.
const KNOWN_PATHS = new Set<string>([
  '/home',
  '/meals',
  '/notes',
  ATTENTION_ROUTES.root, // email destination → the attention surface
  '/study',
  '/chat',
]);

let registered = false;

/** Register built-in intent handlers once. Idempotent. */
export function registerBuiltinIntents(): void {
  if (registered) return;
  registered = true;

  registerIntent('navigate', (args, ctx) => {
    const path = args.path;
    if (typeof path !== 'string' || !KNOWN_PATHS.has(path)) return; // fail-closed
    ctx.navigate(path);
  });

  // NOTE: ctx.activeConversationId is now available for conversation-scoped
  // stale-rejection, but this handler does not yet use it — wiring that guard is
  // a deliberate follow-up.
  registerIntent('chat.openDocument', (args, ctx) => {
    const artifactId = args.artifact_id;
    const conversationId = args.conversation_id;
    if (typeof artifactId !== 'string') return; // fail-closed
    const convId = typeof conversationId === 'string' ? conversationId : '';
    useDocumentViewerStore.getState().openDocument(ctx.surfaceId, artifactId, convId);
  });
}

/** Test-only: allow re-registration after a dispatcher reset. */
export function __resetBuiltinRegistrationForTests(): void {
  registered = false;
}
